import { AutoComplete, Input, InputNumber, Button, Spin, Switch } from 'antd'
import React, { useState, useCallback } from 'react'
import {
  searchSupplierProducts,
  loadMoreSupplierProducts,
} from 'src/features/ManualOrderProductsSlice'
import { resolveManualOrderLineVat } from 'src/utils/manualOrderVatHelpers'

const { Option } = AutoComplete
const isObjectId = value => /^[a-f\d]{24}$/i.test(String(value || '').trim())

const TableColumn = (
  supplierProducts,
  selectedProducts,
  setSelectedProducts,
  dispatch,
  venueId,
  supplierId,
  hasMore,
  currentPage,
  productsLoading,
  isManualSupplier,
) => {
  const [isSearching, setIsSearching] = useState(false)
  const onSelectProduct = (prodId, option) => {
    if (String(prodId).startsWith('__manual_title__:')) {
      const manualTitle = String(prodId).replace('__manual_title__:', '').trim()
      setSelectedProducts(
        selectedProducts.map(prod => {
          if (prod.key === option.key) {
            return {
              ...prod,
              product: '',
              title: manualTitle,
            }
          }
          return prod
        }),
      )
      return
    }

    if (String(prodId).startsWith('__manual_sku__:')) {
      const manualSku = String(prodId).replace('__manual_sku__:', '').trim()
      setSelectedProducts(
        selectedProducts.map(prod => {
          if (prod.key === option.key) {
            return {
              ...prod,
              product: '',
              sku: manualSku,
            }
          }
          return prod
        }),
      )
      return
    }

    const selectedProd = supplierProducts.find(prod => prod._id === prodId)
    if (!selectedProd) return
    setSelectedProducts(
      selectedProducts.map(prod => {
        if (prod.key === option.key) {
          return {
            key: prod.key,
            quantity: 1,
            product: selectedProd._id,
            title: selectedProd.title,
            OMSPrice: selectedProd.OMSPrice,
            price: selectedProd.price,
            sku: selectedProd.sku,
            size: selectedProd.size,
            type: selectedProd.type,
            hasVat: selectedProd.hasVat,
            priceIncludesVat: false,
            nominalCode: selectedProd.nominalCode || '',
          }
        } else return prod
      }),
    )
  }

  const handleSearch = useCallback(
    async (value, order, searchType) => {
      setSelectedProducts(
        selectedProducts.map(prod => {
          if (prod.key === order.key) {
            if (searchType === 'sku') {
              prod.sku = value
            } else {
              prod.title = value
            }
            return prod
          } else return prod
        }),
      )

      if (value.trim() && venueId && isObjectId(supplierId)) {
        setIsSearching(true)
        try {
          await dispatch(
            searchSupplierProducts({
              venueId,
              supplierId,
              searchTerm: value.trim(),
              page: 1,
              pageSize: 200,
              isManualSupplier,
            }),
          )
        } catch (error) {
          console.error('Search error:', error)
        } finally {
          setIsSearching(false)
        }
      }
    },
    [selectedProducts, venueId, supplierId, dispatch],
  )

  const onSkuSearch = (value, order) => {
    handleSearch(value, order, 'sku')
  }

  const onTitleSearch = (value, product) => {
    handleSearch(value, product, 'title')
  }

  const handleLoadMore = useCallback(async () => {
    if (venueId && supplierId && hasMore) {
      try {
        await dispatch(
          loadMoreSupplierProducts({
            venueId,
            supplierId,
            page: currentPage + 1,
            pageSize: 200,
            isManualSupplier,
          }),
        )
      } catch (error) {
        console.error('Load more error:', error)
      }
    }
  }, [venueId, supplierId, hasMore, currentPage, dispatch, isManualSupplier])

  const onChangeQuantity = (value, order) => {
    setSelectedProducts(
      selectedProducts.map(prod => {
        if (prod.key === order.key) {
          prod.quantity = value
          return prod
        } else return prod
      }),
    )
  }

  const onChangePrice = (value, product) => {
    setSelectedProducts(
      selectedProducts.map(prod => {
        if (prod.key === product.key) {
          prod.OMSPrice = value.target.value
          return prod
        } else return prod
      }),
    )
  }
  const onChangeNominalCode = (value, product) => {
    setSelectedProducts(
      selectedProducts.map(prod => {
        if (prod.key === product.key) {
          return {
            ...prod,
            nominalCode: value.target.value,
          }
        }
        return prod
      }),
    )
  }
  const handleRemoveProduct = key => {
    setSelectedProducts(selectedProducts.filter(prod => prod.key !== key))
  }

  const onChangeHasVat = (checked, product) => {
    setSelectedProducts(
      selectedProducts.map(prod => {
        if (prod.key === product.key) {
          return {
            ...prod,
            hasVat: checked,
            priceIncludesVat: checked ? true : false,
          }
        }
        return prod
      }),
    )
  }

  const onChangePriceIncludesVat = (checked, product) => {
    setSelectedProducts(
      selectedProducts.map(prod => {
        if (prod.key === product.key) {
          return {
            ...prod,
            priceIncludesVat: checked,
          }
        }
        return prod
      }),
    )
  }

  const getPriceColumnHint = product => {
    if (!product.hasVat) return 'Enter the price you paid'
    if (product.priceIncludesVat) {
      return 'Receipt price (VAT already included)'
    }
    return 'Ex-VAT price (VAT added on top)'
  }

  const isInvalidRow = row => {
    const title = String(row?.title || '').trim()
    const hasProductId = isObjectId(row?.product)
    const quantity = Number(row?.quantity)
    const unitPrice = Number(row?.OMSPrice)

    return (
      (!hasProductId && !title) ||
      !Number.isFinite(quantity) ||
      quantity <= 0 ||
      !Number.isFinite(unitPrice) ||
      unitPrice < 0
    )
  }

  return [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'key',
      width: 160,
      render: (_, product) => (
        <AutoComplete
          onSelect={onSelectProduct}
          value={product.sku}
          onSearch={val => onSkuSearch(val, product)}
          notFoundContent={
            isSearching || productsLoading ? (
              <Spin size='small' />
            ) : product.sku?.trim() ? (
              `Use "${product.sku.trim()}" as new product SKU`
            ) : (
              'Products not found'
            )
          }
          loading={isSearching || productsLoading}
          placeholder='Search by SKU...'
        >
          {supplierProducts.map(prod => (
            <Option key={product.key} value={prod._id}>
              {prod.sku ? prod.sku : 'No SKU'}
            </Option>
          ))}
          {product.sku?.trim() && (
            <Option
              key={`manual-sku-${product.key}`}
              value={`__manual_sku__:${product.sku.trim()}`}
            >
              Use &quot;{product.sku.trim()}&quot; as new product SKU
            </Option>
          )}
          {hasMore && supplierId && (
            <Option key='load-more-sku' value='load-more' disabled>
              <Button
                type='link'
                size='small'
                onClick={e => {
                  e.stopPropagation()
                  handleLoadMore()
                }}
                loading={productsLoading}
                style={{ width: '100%', textAlign: 'left', padding: 0 }}
              >
                Load More Products...
              </Button>
            </Option>
          )}
        </AutoComplete>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 260,
      render: (_, product) => (
        <AutoComplete
          onSelect={onSelectProduct}
          value={product.title}
          onSearch={val => onTitleSearch(val, product)}
          notFoundContent={
            isSearching || productsLoading ? (
              <Spin size='small' />
            ) : product.title?.trim() ? (
              `Use "${product.title.trim()}" as new product`
            ) : (
              'Products not found'
            )
          }
          loading={isSearching || productsLoading}
          placeholder='Search by title...'
          style={{ width: '100%' }}
        >
          {supplierProducts.map(prod => (
            <Option key={product.key} value={prod._id}>
              {prod.title}
            </Option>
          ))}
          {product.title?.trim() && (
            <Option
              key={`manual-title-${product.key}`}
              value={`__manual_title__:${product.title.trim()}`}
            >
              Use &quot;{product.title.trim()}&quot; as new product
            </Option>
          )}
          {hasMore && supplierId && (
            <Option key='load-more-title' value='load-more' disabled>
              <Button
                type='link'
                size='small'
                onClick={e => {
                  e.stopPropagation()
                  handleLoadMore()
                }}
                loading={productsLoading}
                style={{ width: '100%', textAlign: 'left', padding: 0 }}
              >
                Load More Products...
              </Button>
            </Option>
          )}
        </AutoComplete>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 72,
      render: (_, product) => (
        <div>
          <InputNumber
            min={1}
            precision={0}
            value={product.quantity}
            onChange={value => onChangeQuantity(value, product)}
            placeholder='1'
            style={{ width: '100%' }}
          />
          {(!Number.isFinite(Number(product.quantity)) ||
            Number(product.quantity) <= 0) && (
            <small style={{ color: '#cf1322' }}>Enter quantity 1+</small>
          )}
        </div>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 64,
      render: (_, product) => (
        <span style={{ color: product.size ? '#000' : '#999' }}>
          {product.size || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Unit price',
      dataIndex: 'OMSPrice',
      key: 'OMSPrice',
      width: 120,
      render: (_, product) => (
        <div>
          <Input
            style={{
              width: '100%',
              borderColor:
                Number.isFinite(Number(product.OMSPrice)) &&
                Number(product.OMSPrice) >= 0
                  ? undefined
                  : '#ff7875',
            }}
            value={product.OMSPrice}
            onChange={value => onChangePrice(value, product)}
            placeholder='0.00'
            prefix='£'
          />
          <small style={{ color: '#8c8c8c', display: 'block', marginTop: 4 }}>
            {getPriceColumnHint(product)}
          </small>
          {(!Number.isFinite(Number(product.OMSPrice)) ||
            Number(product.OMSPrice) < 0) && (
            <small style={{ color: '#cf1322' }}>
              Enter a valid price (0 or more)
            </small>
          )}
        </div>
      ),
    },
    {
      title: 'Nominal Code',
      dataIndex: 'nominalCode',
      key: 'nominalCode',
      width: 110,
      render: (_, product) => (
        <Input
          style={{ width: '100%' }}
          value={product.nominalCode || ''}
          onChange={value => onChangeNominalCode(value, product)}
          placeholder='Nominal'
        />
      ),
    },
    {
      title: 'VAT',
      key: 'vat',
      width: 150,
      render: (_, product) => {
        const hasVat = Boolean(product.hasVat)
        const priceIncludesVat = Boolean(product.priceIncludesVat)
        const breakdown = resolveManualOrderLineVat({
          enteredPrice: product.OMSPrice,
          hasVat,
          priceIncludesVat,
          quantity: product.quantity,
        })

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <small style={{ fontWeight: 600, color: 'rgba(0,0,0,0.75)' }}>
                Has VAT?
              </small>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch
                  checked={hasVat}
                  onChange={checked => onChangeHasVat(checked, product)}
                />
                <small style={{ color: '#8c8c8c' }}>
                  {hasVat ? 'Yes' : 'No'}
                </small>
              </div>
            </div>

            {hasVat ? (
              <>
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <small style={{ fontWeight: 600, color: 'rgba(0,0,0,0.75)' }}>
                    VAT included in price?
                  </small>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <Switch
                      checked={priceIncludesVat}
                      onChange={checked =>
                        onChangePriceIncludesVat(checked, product)
                      }
                    />
                    <small style={{ color: '#8c8c8c' }}>
                      {priceIncludesVat ? 'Yes' : 'No'}
                    </small>
                  </div>
                  <small style={{ color: '#8c8c8c' }}>
                    {priceIncludesVat
                      ? 'Total matches the price you entered'
                      : 'Total is higher (VAT added on top)'}
                  </small>
                </div>

                <div
                  style={{
                    padding: '8px 10px',
                    borderRadius: 6,
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    fontSize: 12,
                    lineHeight: 1.6,
                  }}
                >
                  <div>
                    Entered: £{Number(product.OMSPrice || 0).toFixed(2)}
                    {priceIncludesVat ? ' (VAT included)' : ' (ex VAT)'}
                  </div>
                  <div>Ex VAT: £{breakdown.exVatUnit.toFixed(2)}</div>
                  <div>VAT: £{breakdown.vatUnit.toFixed(2)}</div>
                  <div style={{ fontWeight: 600 }}>
                    Total: £{breakdown.grossUnit.toFixed(2)}
                  </div>
                  {Number(product.quantity) > 1 ? (
                    <div>Line total: £{breakdown.grossLine.toFixed(2)}</div>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 112,
      render: (_, product) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            whiteSpace: 'nowrap',
          }}
        >
          <Button
            type='link'
            danger
            size='small'
            style={{ padding: '0 4px', height: 'auto' }}
            onClick={() => handleRemoveProduct(product.key)}
          >
            Delete
          </Button>
          {isInvalidRow(product) && (
            <small
              style={{
                color: '#d46b08',
                whiteSpace: 'normal',
                wordBreak: 'normal',
              }}
            >
              Incomplete row
            </small>
          )}
        </div>
      ),
    },
  ]
}

export default TableColumn
